import React, { useState, useEffect, useCallback } from 'react';
import { ArrowLeftRight, RefreshCw, TrendingUp, Globe, AlertCircle, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const CurrencyConverter = () => {
    const [fromCurrency, setFromCurrency] = useState('USD');
    const [toCurrency, setToCurrency] = useState('TWD');
    const [amount, setAmount] = useState('1');
    const [convertedAmount, setConvertedAmount] = useState('0');
    const [exchangeRate, setExchangeRate] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [lastUpdated, setLastUpdated] = useState('');
    const [error, setError] = useState('');
    const navigate = useNavigate();

    // Popular currencies with Taiwan Dollar included
    const currencies = [
        { code: 'USD', name: 'US Dollar', symbol: '$', flag: '🇺🇸' },
        { code: 'TWD', name: 'Taiwan Dollar', symbol: 'NT$', flag: '🇹🇼' },
        { code: 'EUR', name: 'Euro', symbol: '€', flag: '🇪🇺' },
        { code: 'GBP', name: 'British Pound', symbol: '£', flag: '🇬🇧' },
        { code: 'JPY', name: 'Japanese Yen', symbol: '¥', flag: '🇯🇵' },
        { code: 'CNY', name: 'Chinese Yuan', symbol: '¥', flag: '🇨🇳' },
        { code: 'KRW', name: 'Korean Won', symbol: '₩', flag: '🇰🇷' },
        { code: 'HKD', name: 'Hong Kong Dollar', symbol: 'HK$', flag: '🇭🇰' },
        { code: 'SGD', name: 'Singapore Dollar', symbol: 'S$', flag: '🇸🇬' },
        { code: 'AUD', name: 'Australian Dollar', symbol: 'A$', flag: '🇦🇺' },
        { code: 'CAD', name: 'Canadian Dollar', symbol: 'C$', flag: '🇨🇦' },
        { code: 'CHF', name: 'Swiss Franc', symbol: 'CHF', flag: '🇨🇭' }
    ];

    const fetchExchangeRate = useCallback(async () => {
        if (fromCurrency === toCurrency) {
            setExchangeRate(1);
            setConvertedAmount(amount);
            return;
        }

        setIsLoading(true);
        setError('');

        try {
            // Using CurrencyFreaks API - you'll need to get a free API key
            const response = await fetch(`https://api.currencyfreaks.com/v2.0/rates/latest?apikey=${process.env.REACT_APP_EXCHANGE_API_KEY}&symbols=${toCurrency}&base=${fromCurrency}`);

            if (!response.ok) {
                throw new Error('Failed to fetch exchange rates');
            }

            const data = await response.json();
            const rate = parseFloat(data.rates[toCurrency]);

            setExchangeRate(rate);
            setConvertedAmount((parseFloat(amount || 0) * rate).toFixed(2));
            setLastUpdated(new Date().toLocaleTimeString());

        } catch (err) {
            if (process.env.NODE_ENV === 'development') {
                setError('Unable to fetch live rates. Using demo data.');
            } else {
                setError('#5 Something went wrong !!!');
            }
            // Fallback to demo rates
            const demoRates = {
                'USD-TWD': 31.25, 'TWD-USD': 0.032,
                'USD-EUR': 0.85, 'EUR-USD': 1.18,
                'USD-GBP': 0.73, 'GBP-USD': 1.37,
                'USD-JPY': 110.5, 'JPY-USD': 0.009,
                'USD-CNY': 6.45, 'CNY-USD': 0.155,
                'USD-KRW': 1180, 'KRW-USD': 0.00085,
                'TWD-EUR': 0.027, 'TWD-GBP': 0.023,
                'TWD-JPY': 3.54, 'TWD-CNY': 0.206
            };

            const key = `${fromCurrency}-${toCurrency}`;
            const rate = demoRates[key] || 1;
            setExchangeRate(rate);
            setConvertedAmount((parseFloat(amount || 0) * rate).toFixed(2));
            setLastUpdated(new Date().toLocaleTimeString());
        } finally {
            setIsLoading(false);
        }
    }, [fromCurrency, toCurrency, amount]);


    const useWindowSize = () => {
        const [width, setWidth] = useState(window.innerWidth);

        useEffect(() => {
            const handleResize = () => setWidth(window.innerWidth);
            window.addEventListener('resize', handleResize);

            return () => window.removeEventListener('resize', handleResize);
        }, []);

        return width;
    };

    useEffect(() => {
        fetchExchangeRate();
    }, [fromCurrency, toCurrency, amount, fetchExchangeRate]);

    const handleAmountChange = (e) => {
        const value = e.target.value;
        if (/^\d*\.?\d*$/.test(value)) {
            setAmount(value);
        }
    };

    const swapCurrencies = () => {
        setFromCurrency(toCurrency);
        setToCurrency(fromCurrency);
        setAmount(convertedAmount);
    };

    const getCurrencySymbol = (code) => {
        return currencies.find(c => c.code === code)?.symbol || code;
    };

    const getCurrencyFlag = (code) => {
        return currencies.find(c => c.code === code)?.flag || '🌍';
    };

    // Check if screen is mobile
    const width = useWindowSize();
    const isMobile = width <= 768;


    return (
        <div className="dashboard-container" style={{
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            padding: '20px'
        }}>
            <div className="currency-converter" style={{
                background: 'rgba(255, 255, 255, 0.95)',
                borderRadius: isMobile ? '16px' : '20px',
                padding: isMobile ? '20px' : '30px',
                boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                maxWidth: isMobile ? '80%' : '500px',
                margin: isMobile ? '20px auto' : '40px auto',
                width: isMobile ? 'calc(100% - 20px)' : 'auto'
            }}>
                <button
                    className="exchange-btn"
                    style={{
                        background: 'rgba(255, 255, 255, 0.15)',
                        border: '1px solid rgba(52, 243, 113, 0.3)',
                        borderRadius: '12px',
                        color: '#333',
                        padding: '12px 20px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        cursor: 'pointer',
                        fontWeight: '600',
                        fontSize: '14px',
                        boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)',
                        transition: 'all 0.3s ease',
                        whiteSpace: 'nowrap',
                        backdropFilter: 'blur(10px)',
                        marginBottom: '20px'
                    }}
                    onClick={() => { navigate('/dashboard') }}
                >
                    <ArrowLeft size={18} />
                    <span className="btn-text">Back to Dashboard</span>
                </button>
                {/* Header */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: isMobile ? '20px' : '25px',
                    flexDirection: isMobile ? 'column' : 'row',
                    gap: isMobile ? '12px' : '0'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{
                            width: isMobile ? '40px' : '45px',
                            height: isMobile ? '40px' : '45px',
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            borderRadius: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                            <Globe size={isMobile ? 18 : 20} color="white" />
                        </div>
                        <div>
                            <h2 style={{
                                margin: 0,
                                fontSize: isMobile ? '18px' : '20px',
                                fontWeight: '700',
                                color: '#333',
                                textAlign: isMobile ? 'center' : 'left'
                            }}>
                                Currency Converter
                            </h2>
                            <p style={{
                                margin: 0,
                                fontSize: isMobile ? '12px' : '14px',
                                color: '#666',
                                textAlign: isMobile ? 'center' : 'left'
                            }}>
                                Real-time exchange rates
                            </p>
                        </div>
                    </div>

                    <button
                        onClick={fetchExchangeRate}
                        disabled={isLoading}
                        style={{
                            background: 'rgba(102, 126, 234, 0.1)',
                            border: '1px solid rgba(102, 126, 234, 0.2)',
                            borderRadius: '12px',
                            padding: isMobile ? '8px' : '10px',
                            cursor: isLoading ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.2s ease',
                            opacity: isLoading ? 0.6 : 1
                        }}
                        title="Refresh rates"
                    >
                        <RefreshCw size={isMobile ? 16 : 18} color="#667eea" style={{
                            animation: isLoading ? 'spin 1s linear infinite' : 'none'
                        }} />
                    </button>
                </div>

                {/* Error Alert */}
                {error && (
                    <div style={{
                        background: '#FEF2F2',
                        border: '1px solid #FECACA',
                        borderRadius: '12px',
                        padding: isMobile ? '10px 12px' : '12px 15px',
                        marginBottom: '20px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px'
                    }}>
                        <AlertCircle size={16} color="#EF4444" />
                        <span style={{ fontSize: isMobile ? '12px' : '14px', color: '#DC2626' }}>{error}</span>
                    </div>
                )}

                {/* From Currency */}
                <div style={{ marginBottom: '20px' }}>
                    <label style={{
                        display: 'block',
                        fontSize: isMobile ? '12px' : '14px',
                        fontWeight: '600',
                        color: '#374151',
                        marginBottom: '8px'
                    }}>
                        From
                    </label>
                    <div style={{
                        display: 'flex',
                        gap: isMobile ? '8px' : '12px',
                        flexDirection: isMobile ? 'column' : 'row'
                    }}>
                        <select
                            value={fromCurrency}
                            onChange={(e) => setFromCurrency(e.target.value)}
                            style={{
                                flex: 1,
                                padding: isMobile ? '12px' : '15px',
                                border: '1px solid #E5E7EB',
                                borderRadius: '12px',
                                fontSize: isMobile ? '14px' : '16px',
                                fontWeight: '500',
                                background: '#F9FAFB',
                                outline: 'none',
                                transition: 'all 0.2s ease'
                            }}
                        >
                            {currencies.map(currency => (
                                <option key={currency.code} value={currency.code}>
                                    {currency.flag} {currency.code} - {currency.name}
                                </option>
                            ))}
                        </select>
                        <input
                            type="text"
                            value={amount}
                            onChange={handleAmountChange}
                            placeholder="0.00"
                            style={{
                                width: isMobile ? 'unset' : '120px',
                                padding: isMobile ? '12px' : '15px',
                                border: '1px solid #E5E7EB',
                                borderRadius: '12px',
                                fontSize: isMobile ? '14px' : '16px',
                                fontWeight: '600',
                                textAlign: 'right',
                                background: 'white',
                                outline: 'none',
                                transition: 'all 0.2s ease'
                            }}
                        />
                    </div>
                </div>

                {/* Swap Button */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    marginBottom: '20px'
                }}>
                    <button
                        onClick={swapCurrencies}
                        style={{
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            border: 'none',
                            borderRadius: '50%',
                            width: isMobile ? '40px' : '45px',
                            height: isMobile ? '40px' : '45px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)',
                            transition: 'all 0.3s ease',
                            transform: 'rotate(0deg)'
                        }}
                        onMouseEnter={(e) => e.target.style.transform = 'rotate(180deg) scale(1.1)'}
                        onMouseLeave={(e) => e.target.style.transform = 'rotate(0deg) scale(1)'}
                    >
                        <ArrowLeftRight size={isMobile ? 16 : 18} color="white" />
                    </button>
                </div>

                {/* To Currency */}
                <div style={{ marginBottom: '20px' }}>
                    <label style={{
                        display: 'block',
                        fontSize: isMobile ? '12px' : '14px',
                        fontWeight: '600',
                        color: '#374151',
                        marginBottom: '8px'
                    }}>
                        To
                    </label>
                    <div style={{
                        display: 'flex',
                        gap: isMobile ? '8px' : '12px',
                        flexDirection: isMobile ? 'column' : 'row'
                    }}>
                        <select
                            value={toCurrency}
                            onChange={(e) => setToCurrency(e.target.value)}
                            style={{
                                flex: 1,
                                padding: isMobile ? '12px' : '15px',
                                border: '1px solid #E5E7EB',
                                borderRadius: '12px',
                                fontSize: isMobile ? '14px' : '16px',
                                fontWeight: '500',
                                background: '#F9FAFB',
                                outline: 'none',
                                transition: 'all 0.2s ease'
                            }}
                        >
                            {currencies.map(currency => (
                                <option key={currency.code} value={currency.code}>
                                    {currency.flag} {currency.code} - {currency.name}
                                </option>
                            ))}
                        </select>
                        <div style={{
                            width: isMobile ? 'unset' : '120px',
                            padding: isMobile ? '12px' : '15px',
                            border: '2px solid #667eea',
                            borderRadius: '12px',
                            fontSize: isMobile ? '14px' : '16px',
                            fontWeight: '700',
                            textAlign: 'right',
                            background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)',
                            color: '#667eea',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end'
                        }}>
                            {isLoading ? (
                                <div style={{
                                    width: '16px',
                                    height: '16px',
                                    border: '2px solid rgba(102, 126, 234, 0.3)',
                                    borderTop: '2px solid #667eea',
                                    borderRadius: '50%',
                                    animation: 'spin 1s linear infinite'
                                }} />
                            ) : (
                                convertedAmount
                            )}
                        </div>
                    </div>
                </div>

                {/* Exchange Rate Info */}
                <div style={{
                    background: '#F9FAFB',
                    borderRadius: '12px',
                    padding: isMobile ? '15px' : '20px',
                    marginBottom: '20px'
                }}>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '10px',
                        flexDirection: isMobile ? 'column' : 'row',
                        gap: isMobile ? '8px' : '0'
                    }}>
                        <span style={{ fontSize: isMobile ? '12px' : '14px', color: '#666' }}>Exchange Rate</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                            <TrendingUp size={14} color="#10B981" />
                            <span style={{ fontSize: isMobile ? '12px' : '14px', fontWeight: '600', color: '#333' }}>
                                1 {getCurrencyFlag(fromCurrency)} {fromCurrency} = {exchangeRate.toLocaleString()} {getCurrencyFlag(toCurrency)} {toCurrency}
                            </span>
                        </div>
                    </div>

                    {lastUpdated && (
                        <div style={{ fontSize: isMobile ? '10px' : '12px', color: '#9CA3AF', textAlign: 'center' }}>
                            Last updated: {lastUpdated}
                        </div>
                    )}
                </div>

                {/* Quick Convert Buttons */}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
                    gap: isMobile ? '6px' : '8px'
                }}>
                    {['1', '10', '100', '1000'].map(value => (
                        <button
                            key={value}
                            onClick={() => setAmount(value)}
                            style={{
                                padding: isMobile ? '8px' : '10px',
                                border: '1px solid #E5E7EB',
                                borderRadius: '8px',
                                background: amount === value ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'white',
                                color: amount === value ? 'white' : '#666',
                                fontSize: isMobile ? '12px' : '14px',
                                fontWeight: '500',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease'
                            }}
                        >
                            {getCurrencySymbol(fromCurrency)}{value}
                        </button>
                    ))}
                </div>

                <style>
                    {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
            .exchange-btn:hover {
              background: rgba(143, 250, 81, 0.2) !important;
              transform: translateY(-1px);
              box-shadow: 0 6px 20px rgba(78, 239, 118, 0.2) !important;
            }
        `}
                </style>
            </div>
        </div>
    );
};

export default CurrencyConverter;